"use client";

import React, { useCallback } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function PartnerHero() {
  const handleSmoothScroll = useCallback((targetId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth"
      });
    } else {
      window.location.hash = targetId;
    }
  }, []);
  return <section id="b2bpartnerhero" data-nav="dark" className="dark relative bg-background text-foreground overflow-hidden py-20 md:py-28 lg:py-32">
      <div id="hero-baogia" className="absolute -top-24 pointer-events-none" aria-hidden="true" />

      {/* Blueprint grid overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)/0.12_1px,transparent_1px),linear-gradient(to_bottom,var(--border)/0.12_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none" aria-hidden="true" />

      {/* Subtle circuit line decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <svg className="absolute -top-12 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full opacity-20" viewBox="0 0 1200 800" fill="none">
          <path d="M100 200 H400 L450 250 H750 L800 200 H1100" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 6" className="text-border" />
          <path d="M200 600 H500 L550 550 H850 L900 600 H1050" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" className="text-border" />
          <circle cx="450" cy="250" r="4" className="fill-accent" />
          <circle cx="750" cy="250" r="4" className="fill-primary" />
          <circle cx="550" cy="550" r="4" className="fill-accent" />
          <circle cx="850" cy="550" r="4" className="fill-primary" />
        </svg>

        {/* Soft atmospheric gradient accents */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[38rem] h-[18rem] bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Main headline */}
        <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-tight max-w-4xl mx-auto">
          BÁO GIÁ ĐỐI TÁC ROBOTO
        </h1>

        {/* Supporting subtext */}
        <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          Giải pháp Robotics, STEM, Coding và AI dành cho Nhà trường & Trung tâm
        </p>

        {/* Action buttons */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-8 py-6 rounded-md shadow-md text-base">
            <Link to="/FreeTrialContactPage">
              ĐĂNG KÝ TƯ VẤN
            </Link>
          </Button>

          <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-accent/10 hover:text-accent font-semibold px-8 py-6 rounded-md text-base">
            <Link to="#contact-cta" onClick={handleSmoothScroll("contact-cta")}>
              LIÊN HỆ ROBOTO
            </Link>
          </Button>
        </div>

        {/* Partner Type Selector cards */}
        <div className="mt-16 lg:mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto text-left">
          {/* Panel 1 */}
          <Link to="#pricing-trung-tam" onClick={handleSmoothScroll("pricing-trung-tam")} className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform hover:-translate-y-1">
            <Card className="h-full bg-card/60 backdrop-blur-xs border-border hover:border-accent/70 transition-colors p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <h2 className="font-serif font-bold text-2xl text-foreground tracking-tight">
                  DÀNH CHO TRUNG TÂM
                </h2>
                <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed">
                  Giải pháp triển khai Robotics & Công nghệ tại các trung tâm giáo dục
                </p>
              </div>
              <div className="mt-6 flex items-center gap-2 text-sm font-medium text-foreground group-hover:text-accent transition-colors">
                <span className="sr-only">Xem báo giá trung tâm</span>
                <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
              </div>
            </Card>
          </Link>

          {/* Panel 2 */}
          <Link to="#pricing-nha-truong" onClick={handleSmoothScroll("pricing-nha-truong")} className="group block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-transform hover:-translate-y-1">
            <Card className="h-full bg-card/60 backdrop-blur-xs border-border hover:border-accent/70 transition-colors p-8 lg:p-10 flex flex-col justify-between">
              <div>
                <h2 className="font-serif font-bold text-2xl text-foreground tracking-tight">
                  DÀNH CHO NHÀ TRƯỜNG
                </h2>
                <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed">
                  Giải pháp chương trình Robotics, STEM, Coding & AI trong nhà trường
                </p>
              </div>
              <div className="mt-6 flex items-center gap-2 text-sm font-medium text-foreground group-hover:text-accent transition-colors">
                <span className="sr-only">Xem báo giá nhà trường</span>
                <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
              </div>
            </Card>
          </Link>
        </div>
      </div>
    </section>;
}